import {
    getPersona,
    listPersonas,
} from './queries';
import {
    createPersona,
    deletePersona,
    updatePersona,
} from './mutations';

import {GraphQLQuery} from '@aws-amplify/api';

import {
    CreatePersona,
    DeletePersona,
    GetPersona,
    ListPersonas,
    PersonaFormValues,
    PersonaLifecycle,
    UpdatePersona,
    WeightedActionCode,
} from '../types.ts';
import {getTestingApiClient} from '../../testing-api-integration/client.ts';

// The form keeps lifecycle flat and possibly blank. PersonaLifecycleInput has
// three NonNull Ints, so a partially-filled lifecycle cannot be sent — it is
// either complete or omitted entirely (the field is nullable on the parent).
const toLifecycleInput = (lifecycle: PersonaFormValues['lifecycle']): PersonaLifecycle | undefined => {
    const {
        wakeCadenceHours,
        expectedLifespanDays,
        cancellationLikelihoodPercent,
    } = lifecycle ?? {};

    const values = [wakeCadenceHours, expectedLifespanDays, cancellationLikelihoodPercent];

    if (values.some(value => value === '' || value === null || typeof value === 'undefined')) {
        return undefined;
    }

    return {
        wakeCadenceHours: Number(wakeCadenceHours),
        expectedLifespanDays: Number(expectedLifespanDays),
        cancellationLikelihoodPercent: Number(cancellationLikelihoodPercent),
    };
};

// The test definitions this persona runs. Blank entries are dropped — the
// MultiSelect cannot produce them, but a record loaded from the API can carry a
// null inside the list (`[ID]` is a nullable list of NULLABLE ids).
const toTestDefinitionIdsInput = (testDefinitionIds?: (string | null)[] | null): string[] =>
    (testDefinitionIds ?? [])
        .filter((id): id is string => typeof id === 'string' && id.trim() !== '')
        .map(id => id.trim());

// Strip the __typename AppSync returns on nested objects — sending it back
// inside WeightedActionCodeInput would be rejected as an unknown field.
const toActionCodesInput = (actionCodes?: WeightedActionCode[] | null): WeightedActionCode[] =>
    (actionCodes ?? [])
        .filter(entry => entry.actionCode.trim() !== '')
        .map(entry => ({
            actionCode: entry.actionCode.trim(),
            weight: Number(entry.weight),
        }));

export const fetchRecords = async (filter: object) => {
    const response = await getTestingApiClient().graphql<GraphQLQuery<ListPersonas>>({
        query: listPersonas,
        variables: {filter: filter},
    });
    return response.data?.listPersonas.items ?? [];
};

export const fetch = async (id: string) => {
    const response = await getTestingApiClient().graphql<GraphQLQuery<GetPersona>>({
        query: getPersona,
        variables: {id: id},
    });
    return response.data?.getPersona;
};

export const add = async (payload: PersonaFormValues) => {
    const input = {
        name: payload.name,
        description: payload.description || undefined,
        // DEPRECATED but still sent — see the note on WeightedActionCode.
        actionCodes: toActionCodesInput(payload.actionCodes),
        // What the executor actually runs.
        testDefinitionIds: toTestDefinitionIdsInput(payload.testDefinitionIds),
        lifecycle: toLifecycleInput(payload.lifecycle),
    };

    const response = await getTestingApiClient().graphql<GraphQLQuery<CreatePersona>>({
        query: createPersona,
        variables: {input: input},
    });
    return response.data?.createPersona;
};

export const update = async (id: string, record: PersonaFormValues) => {
    // updatePersona SETs name, description, actionCodes and lifecycle
    // unconditionally, so every field is always sent — a partial payload would
    // blank the omitted ones. This is also what lets a record stored with
    // actionCodes: NULL / lifecycle: NULL be filled in rather than only replaced.
    const input = {
        id: id,
        name: record.name,
        description: record.description || undefined,
        // DEPRECATED but still sent — see the note on WeightedActionCode.
        actionCodes: toActionCodesInput(record.actionCodes),
        // What the executor actually runs. Sent unconditionally like the rest:
        // updatePersona SETs it, so omitting it would blank the selection.
        testDefinitionIds: toTestDefinitionIdsInput(record.testDefinitionIds),
        lifecycle: toLifecycleInput(record.lifecycle),
    };

    const response = await getTestingApiClient().graphql<GraphQLQuery<UpdatePersona>>({
        query: updatePersona,
        variables: {input: input},
    });
    return response.data?.updatePersona;
};

export const deleteRecord = async (id: string) => {
    const response = await getTestingApiClient().graphql<GraphQLQuery<DeletePersona>>({
        query: deletePersona,
        variables: {input: {id: id}},
    });
    return response.data?.deletePersona;
};
